import { useState, useEffect, type Dispatch, type SetStateAction } from 'react'
import { z } from 'zod'
import { BRAND } from '@/lib/config/brand'

/**
 * Options for usePersistedState hook
 */
export interface PersistedStateOptions<T> {
  /** localStorage key */
  key: string
  /** Default value if nothing in localStorage */
  defaultValue: T
  /** Zod schema for runtime validation */
  schema?: z.ZodSchema<T>
  /** Version number for migrations (default: 1) */
  version?: number
  /** Migration function to transform old data format */
  migrate?: (oldValue: any, oldVersion: number) => T
  /** Custom validation function */
  validate?: (value: T) => boolean
  /** Debounce localStorage writes (ms). State updates remain synchronous. */
  persistDebounceMs?: number
}

/**
 * Custom hook that persists state to localStorage with validation and migration support
 * 
 * @example
 * ```tsx
 * const [value, setValue] = usePersistedState({
 *   key: StorageKeys.UI_SHOW_SIDEBAR,
 *   defaultValue: true,
 *   schema: StorageSchemas.boolean,
 * })
 * ```
 * 
 * @example With migration
 * ```tsx
 * const [sizes, setSizes] = usePersistedState({
 *   key: StorageKeys.UI_PANEL_SIZES,
 *   defaultValue: [20, 55, 25],
 *   schema: StorageSchemas.panelSizes,
 *   version: 2,
 *   migrate: (oldValue, oldVersion) => {
 *     if (oldVersion === 1 && Array.isArray(oldValue) && oldValue.length === 2) {
 *       return [oldValue[0], oldValue[1], 25] // Add third panel
 *     }
 *     return oldValue
 *   },
 * })
 * ```
 */
export function usePersistedState<T>(
  options: PersistedStateOptions<T>
): [T, Dispatch<SetStateAction<T>>]
export function usePersistedState<T>(
  key: string,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>]
export function usePersistedState<T>(
  keyOrOptions: string | PersistedStateOptions<T>,
  defaultValueParam?: T
): [T, Dispatch<SetStateAction<T>>] {
  // Support both old API (backward compatible) and new API
  const options: PersistedStateOptions<T> = 
    typeof keyOrOptions === 'string'
      ? { key: keyOrOptions, defaultValue: defaultValueParam! }
      : keyOrOptions

  const { 
    key, 
    defaultValue, 
    schema, 
    version = 1, 
    migrate, 
    validate,
    persistDebounceMs = 0,
  } = options

  // Initialize state from localStorage or use default
  const [state, setState] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key)
      if (item) {
        const parsed = JSON.parse(item)
        
        // Check if stored value has version metadata
        const storedVersion = parsed._version !== undefined ? parsed._version : 0
        const storedValue = parsed._version !== undefined ? parsed.value : parsed
        
        // Migrate if version mismatch
        let value = storedValue
        if (storedVersion !== version && migrate) {
          try {
            value = migrate(storedValue, storedVersion)
          } catch (error) {
            console.warn(`[Storage] Migration failed for ${key}, using default:`, error)
            return defaultValue
          }
        }
        
        // Handle Set serialization (stored as array)
        if (defaultValue instanceof Set && Array.isArray(value)) {
          value = new Set(value) as T
        }
        
        // Validate with schema if provided
        if (schema) {
          const result = schema.safeParse(value)
          if (result.success) {
            return result.data
          } else {
            console.warn(`[Storage] Invalid value for ${key}, using default:`, result.error)
            return defaultValue
          }
        }
        
        // Custom validation
        if (validate && !validate(value as T)) {
          console.warn(`[Storage] Validation failed for ${key}, using default`)
          return defaultValue
        }
        
        return value as T
      }
      return defaultValue
    } catch (error) {
      console.warn(`[Storage] Error loading ${key}:`, error)
      return defaultValue
    }
  })

  // Update localStorage whenever state changes (optional debounce for hot paths)
  useEffect(() => {
    const write = () => {
      try {
        const valueToStore = state instanceof Set ? Array.from(state) : state
        const stored = {
          _version: version,
          value: valueToStore,
        }
        window.localStorage.setItem(key, JSON.stringify(stored))
      } catch (error) {
        console.warn(`[Storage] Error saving ${key}:`, error)

        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          console.warn('[Storage] localStorage quota exceeded, clearing old data')
          try {
            const allKeys = Object.keys(localStorage)
            for (const k of allKeys) {
              if (k.startsWith(`${BRAND.storagePrefix}_`) && k !== key) {
                localStorage.removeItem(k)
              }
            }
            const valueToStore = state instanceof Set ? Array.from(state) : state
            const stored = {
              _version: version,
              value: valueToStore,
            }
            window.localStorage.setItem(key, JSON.stringify(stored))
          } catch (retryError) {
            console.error('[Storage] Failed to save state even after clearing old data:', retryError)
          }
        }
      }
    }

    if (persistDebounceMs <= 0) {
      write()
      return
    }

    const t = window.setTimeout(write, persistDebounceMs)
    return () => window.clearTimeout(t)
  }, [key, state, version, persistDebounceMs])

  return [state, setState]
}

/**
 * Clear all app localStorage data
 * @deprecated Use StorageManager.clearAll() instead
 */
export function clearPersistedState() {
  const allKeys = Object.keys(localStorage)
  for (const key of allKeys) {
    if (key.startsWith(`${BRAND.storagePrefix}_`)) {
      localStorage.removeItem(key)
    }
  }
}

/**
 * Get all app persisted state for debugging
 * @deprecated Use StorageManager.export() instead
 */
export function getPersistedState() {
  const state: Record<string, any> = {}
  const allKeys = Object.keys(localStorage)
  for (const key of allKeys) {
    if (key.startsWith(`${BRAND.storagePrefix}_`)) {
      try {
        const item = localStorage.getItem(key)
        if (item) {
          const parsed = JSON.parse(item)
          // Extract value if stored with version metadata
          state[key] = parsed._version !== undefined ? parsed.value : parsed
        } else {
          state[key] = null
        }
      } catch {
        state[key] = localStorage.getItem(key)
      }
    }
  }
  return state
}
